// 🔥 Auto-Tracking Firebase Operations
// This module automatically tracks Firebase operations without requiring code changes

import { 
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment,
  arrayUnion,
  serverTimestamp,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';

// Import tracking functions
import { trackOperation, executeWithTracking } from './usageTracker';
import errorNotificationService from '../services/ErrorNotificationService';

// Global company ID for tracking
let globalCompanyId = null;
// Avoid circular import with firebase.config (db may be undefined at module init)
let trackingDb = null;

export const setTrackingDb = (dbInstance) => {
  trackingDb = dbInstance;
};

const resolveTrackingDb = async () => {
  if (trackingDb) return trackingDb;
  const mod = await import('../firebase.config');
  trackingDb = mod.db;
  return trackingDb;
};

const trackForCompany = async (operationType, count = 1) => {
  if (!globalCompanyId) return;
  const dbInstance = await resolveTrackingDb();
  if (!dbInstance) {
    console.warn('⚠️ Tracking skipped: Firestore db is not ready');
    return;
  }
  await trackOperation(dbInstance, globalCompanyId, operationType, count);
};

// Set the global company ID for tracking
export const setGlobalCompanyId = (companyId) => {
  if (globalCompanyId !== companyId) {
    clearSnapshotBillingRegistry();
  }
  globalCompanyId = companyId;
};

// Get current company ID
export const getCurrentCompanyId = () => globalCompanyId;

// Auto-tracking wrapper for getDoc
const originalGetDoc = getDoc;
const trackedGetDoc = async (docRef, options = {}) => {
  try {
    const result = await originalGetDoc(docRef);
    
    // Auto-track if we have a company ID and not skipping
    if (globalCompanyId && !options.skipTracking && isServerSourcedDoc(result)) {
      await trackForCompany('reads', 1);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Auto-tracked getDoc error:', error);
    // Use ErrorNotificationService for error handling
    errorNotificationService.handleError(error, {
      context: 'AutoTrackingFirebase',
      showNotification: false // Don't show notification for low-level errors, let components handle
    });
    throw error;
  }
};

// Auto-tracking wrapper for getDocs
const originalGetDocs = getDocs;
const trackedGetDocs = async (querySnapshot, options = {}) => {
  try {
    const result = await originalGetDocs(querySnapshot);
    
    // Auto-track if we have a company ID and not skipping
    if (globalCompanyId && !options.skipTracking && isServerSourcedSnapshot(result)) {
      const count = result.docs.length;
      if (count > 0) {
        await trackForCompany('reads', count);
      }
    }
    
    return result;
  } catch (error) {
    console.error('❌ Auto-tracked getDocs error:', error);
    throw error;
  }
};

// Prevent re-billing the same collection on every listener mount / page navigation.
const SNAPSHOT_INITIAL_BILL_COOLDOWN_MS = 30 * 60 * 1000; // 30 min per collection path
const snapshotBillingRegistry = new Map();

export const clearSnapshotBillingRegistry = () => {
  snapshotBillingRegistry.clear();
};

function isServerSourcedSnapshot(snapshot) {
  return snapshot?.metadata?.fromCache === false;
}

function isServerSourcedDoc(snap) {
  return snap?.metadata?.fromCache === false;
}

function getSnapshotListenerKey(refOrQuery) {
  try {
    if (refOrQuery?._query?.path) {
      const segments = refOrQuery._query.path.segments;
      if (Array.isArray(segments)) {
        return `query:${segments.join('/')}`;
      }
    }
    if (refOrQuery?.path) {
      const path = refOrQuery.path;
      const depth = path.split('/').filter(Boolean).length;
      const isDoc = depth % 2 === 0;
      return isDoc ? `doc:${path}` : `col:${path}`;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

/**
 * Bill snapshot reads aligned with Firebase billing:
 * - Cache snapshots (metadata.fromCache=true) are FREE in Firebase — do not count.
 * - Server snapshots: bill docChanges only on confirmed server data.
 * - Bulk initial sync per collection path: once per cooldown window (dedupe remounts).
 */
function countBillableSnapshotReads(snapshot, listenerKey) {
  if (!isServerSourcedSnapshot(snapshot)) {
    return 0;
  }

  const changes =
    typeof snapshot?.docChanges === 'function'
      ? snapshot.docChanges({ includeMetadataChanges: false })
      : [];

  if (!Array.isArray(changes) || changes.length === 0) {
    return 0;
  }

  const allAdded = changes.every((c) => c.type === 'added');
  const isBulkInitialSync =
    allAdded && (snapshot.size === 0 || changes.length >= snapshot.size);

  if (!listenerKey) {
    return changes.length;
  }

  const now = Date.now();
  const reg = snapshotBillingRegistry.get(listenerKey) || { lastInitialBillAt: 0 };

  if (isBulkInitialSync) {
    if (now - reg.lastInitialBillAt < SNAPSHOT_INITIAL_BILL_COOLDOWN_MS) {
      return 0;
    }
    reg.lastInitialBillAt = now;
    reg.lastBilledCount = changes.length;
    snapshotBillingRegistry.set(listenerKey, reg);
    return changes.length;
  }

  return changes.length;
}

const originalOnSnapshot = onSnapshot;
const withSnapshotTracking = (handler, listenerKey, options = {}) => {
  if (typeof handler !== 'function') return handler;
  return (snapshot) => {
    if (globalCompanyId && !options.skipTracking) {
      try {
        const billable = countBillableSnapshotReads(snapshot, listenerKey);
        if (billable > 0) {
          trackForCompany('reads', billable).catch((err) => {
            console.error('Failed to track snapshot reads:', err);
          });
        }
      } catch (err) {
        console.error('Snapshot tracking error:', err);
      }
    }
    return handler(snapshot);
  };
};

const trackedOnSnapshot = (refOrQuery, ...args) => {
  const listenerKey = getSnapshotListenerKey(refOrQuery);

  // Handles all Firebase overloads:
  // onSnapshot(ref, next, error?, complete?)
  // onSnapshot(ref, options, next, error?, complete?)
  // onSnapshot(ref, observer)
  // onSnapshot(ref, options, observer)
  if (args.length >= 1 && typeof args[0] === 'object' && args[0] !== null && typeof args[0].next === 'function') {
    const observer = args[0];
    return originalOnSnapshot(refOrQuery, {
      ...observer,
      next: withSnapshotTracking(observer.next, listenerKey)
    });
  }

  if (args.length >= 2 && typeof args[0] === 'object' && args[0] !== null && typeof args[1] === 'object' && args[1] !== null && typeof args[1].next === 'function') {
    const options = args[0];
    const observer = args[1];
    const rest = args.slice(2);
    return originalOnSnapshot(refOrQuery, options, {
      ...observer,
      next: withSnapshotTracking(observer.next, listenerKey, options)
    }, ...rest);
  }

  // Function callback overloads
  const maybeOptions = typeof args[0] === 'object' && args[0] !== null && typeof args[0] !== 'function' ? args[0] : null;
  if (maybeOptions) {
    const next = args[1];
    const wrappedNext = withSnapshotTracking(next, listenerKey, maybeOptions);
    return originalOnSnapshot(refOrQuery, maybeOptions, wrappedNext, ...args.slice(2));
  }

  const next = args[0];
  const wrappedNext = withSnapshotTracking(next, listenerKey);
  return originalOnSnapshot(refOrQuery, wrappedNext, ...args.slice(1));
};

// Auto-tracking wrapper for setDoc
const originalSetDoc = setDoc;
const trackedSetDoc = async (docRef, data, options = {}) => {
  try {
    const result = await originalSetDoc(docRef, data, options);
    
    // Auto-track if we have a company ID and not skipping
    if (globalCompanyId && !options.skipTracking) {
      await trackForCompany('writes', 1);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Auto-tracked setDoc error:', error);
    throw error;
  }
};

// Auto-tracking wrapper for updateDoc
const originalUpdateDoc = updateDoc;
const trackedUpdateDoc = async (docRef, data, options = {}) => {
  try {
    const result = await originalUpdateDoc(docRef, data);
    
    // Auto-track if we have a company ID and not skipping
    if (globalCompanyId && !options.skipTracking) {
      await trackForCompany('writes', 1);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Auto-tracked updateDoc error:', error);
    // Use ErrorNotificationService for error handling (but don't show notification at low level)
    errorNotificationService.handleError(error, {
      context: 'AutoTrackingFirebase.updateDoc',
      showNotification: false // Let components handle notifications
    });
    throw error;
  }
};

// Auto-tracking wrapper for deleteDoc
const originalDeleteDoc = deleteDoc;
const trackedDeleteDoc = async (docRef, options = {}) => {
  try {
    const result = await originalDeleteDoc(docRef);
    
    // Auto-track if we have a company ID and not skipping
    if (globalCompanyId && !options.skipTracking) {
      await trackForCompany('deletes', 1);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Auto-tracked deleteDoc error:', error);
    throw error;
  }
};

// Auto-tracking wrapper for addDoc
const originalAddDoc = addDoc;
const trackedAddDoc = async (collectionRef, data, options = {}) => {
  try {
    const result = await originalAddDoc(collectionRef, data);
    
    // Auto-track if we have a company ID and not skipping
    if (globalCompanyId && !options.skipTracking) {
      await trackForCompany('writes', 1);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Auto-tracked addDoc error:', error);
    throw error;
  }
};

// Tracked writeBatch that counts operations
const originalWriteBatch = writeBatch;
export const trackedWriteBatch = (dbInstance) => {
  const batch = originalWriteBatch(dbInstance);
  const originalCommit = batch.commit.bind(batch);
  
  let operationCount = 0;
  
  // Wrap set/update/delete to count operations
  const originalSet = batch.set.bind(batch);
  batch.set = (...args) => {
    operationCount++;
    return originalSet(...args);
  };
  
  const originalUpdate = batch.update.bind(batch);
  batch.update = (...args) => {
    operationCount++;
    return originalUpdate(...args);
  };
  
  const originalDelete = batch.delete.bind(batch);
  batch.delete = (...args) => {
    operationCount++;
    return originalDelete(...args);
  };
  
  // Override commit to track operations
  batch.commit = async () => {
    const result = await originalCommit();
    
    // Track batch operations
    if (globalCompanyId && operationCount > 0) {
      try {
        // Track as multiple write operations
        for (let i = 0; i < operationCount; i++) {
          await trackOperation(dbInstance, globalCompanyId, 'writes');
        }
      } catch (error) {
        console.error('Error tracking batch operations:', error);
      }
    }
    
    return result;
  };
  
  return batch;
};

// Export the tracked versions as the main functions
export {
  // Auto-tracking Firebase operations
  trackedGetDoc as getDoc,
  trackedGetDocs as getDocs,
  trackedSetDoc as setDoc,
  trackedUpdateDoc as updateDoc,
  trackedDeleteDoc as deleteDoc,
  trackedAddDoc as addDoc,
  trackedWriteBatch as writeBatch,
  // Re-export all other Firebase functions unchanged
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment,
  arrayUnion,
  serverTimestamp,
  trackedOnSnapshot as onSnapshot
};

// Debug function to check tracking status
export const getTrackingStatus = () => {
  return {
    companyId: globalCompanyId,
    isTracking: globalCompanyId !== null,
    snapshotListenersBilled: snapshotBillingRegistry.size
  };
};

// Stop tracking
export const stopTracking = () => {
  globalCompanyId = null;
  clearSnapshotBillingRegistry();
};
